// ─────────────────────────────────────────────────────────────────────────────
// Price Observations Actions
// ─────────────────────────────────────────────────────────────────────────────
// NgRx actions for the priceObservations store slice.
// Covers list, update, delete, bulk-delete, keep-price, the import journal,
// loading state, search and filters.
// ─────────────────────────────────────────────────────────────────────────────

import { createAction, props } from '@ngrx/store';
import { APIResponse } from 'src/app/core/models/api-response';
import { IPageCount } from 'src/app/shared/models/page.model';
import {
  IPriceImport,
  IPriceObservation,
  IPriceObservationFilters,
  IUpdatePriceObservationPayload,
} from '../models/price-observation.models';

// ── Load observations ────────────────────────────────────────────────────────

export const LoadPriceObservations = createAction(
  '[ PriceObservations ] - LoadPriceObservations',
  props<{
    page: IPageCount;
    key?: string;
    filters?: Partial<IPriceObservationFilters>;
    sortBy?: string;
    sortMode?: 'asc' | 'desc';
  }>(),
);
export const LoadPriceObservationsSuccess = createAction(
  '[ PriceObservations ] - LoadPriceObservations Success ',
  props<{ response: APIResponse<IPriceObservation> }>(),
);
export const LoadPriceObservationsError = createAction('[ PriceObservations ] - LoadPriceObservations Error');

// ── Update ───────────────────────────────────────────────────────────────────

export const UpdatePriceObservation = createAction(
  '[ PriceObservation ] - UpdatePriceObservation',
  props<{ id: number; payload: IUpdatePriceObservationPayload }>(),
);
export const UpdatePriceObservationSuccess = createAction(
  '[ PriceObservation ] - UpdatePriceObservation Success ',
  props<{ observation: IPriceObservation }>(),
);
export const UpdatePriceObservationError = createAction('[ PriceObservation ] - UpdatePriceObservation Error', props<{ error: string }>());

// ── Keep price (settles a conflict) ──────────────────────────────────────────

export const KeepPriceObservation = createAction(
  '[ PriceObservation ] - KeepPrice',
  props<{ id: number }>(),
);
export const KeepPriceObservationSuccess = createAction(
  '[ PriceObservation ] - KeepPrice Success ',
  props<{ observation: IPriceObservation }>(),
);
export const KeepPriceObservationError = createAction(
  '[ PriceObservation ] - KeepPrice Error',
  props<{ error: string }>(),
);

export const DeletePriceObservation = createAction('[ PriceObservation ] - DeletePriceObservation', props<{ id: number }>());
export const DeletePriceObservationSuccess = createAction('[ PriceObservation ] - DeletePriceObservation Success ');
export const DeletePriceObservationError = createAction('[ PriceObservation ] - DeletePriceObservation Error');

export const BulkDeletePriceObservations = createAction('[ PriceObservations ] - BulkDelete', props<{ ids: number[] }>());
export const BulkDeletePriceObservationsSuccess = createAction('[ PriceObservations ] - BulkDelete Success ');
export const BulkDeletePriceObservationsError = createAction('[ PriceObservations ] - BulkDelete Error');

// ── Import journal ───────────────────────────────────────────────────────────

export const LoadPriceImports = createAction('[ PriceImports ] - LoadPriceImports', props<{ page: IPageCount; state?: string }>());
export const LoadPriceImportsSuccess = createAction('[ PriceImports ] - LoadPriceImports Success ', props<{ response: APIResponse<IPriceImport> }>());
export const LoadPriceImportsError = createAction('[ PriceImports ] - LoadPriceImports Error');

// ── Loader ───────────────────────────────────────────────────────────────────

export const SetPriceObservationsLoader = createAction('[ PriceObservations ] - SetLoading', props<{ isLoading: boolean }>());

// ── Search ───────────────────────────────────────────────────────────────────

export const SetPriceObservationSearch = createAction('[ PriceObservations ] - SetSearch', props<{ keyword: string }>());

// ── Filters ──────────────────────────────────────────────────────────────────

export const SetPriceObservationFilters = createAction(
  '[ PriceObservations ] - SetFilters',
  props<{ filters: Partial<IPriceObservationFilters> }>(),
);
